<template>
  <div class="vessel-list-container">
    <div class="page-header">
      <h2>船舶列表</h2>
    </div>

    <!-- 搜索区域 -->
    <el-card class="search-box">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="MMSI号">
          <el-input v-model="searchForm.mmsi" placeholder="请输入MMSI号" clearable></el-input>
        </el-form-item>
        <el-form-item label="船舶名称">
          <el-input v-model="searchForm.vesselName" placeholder="请输入船舶名称" clearable></el-input>
        </el-form-item>
        <el-form-item label="船舶类型">
          <el-select v-model="searchForm.vesselType" placeholder="请选择船舶类型" clearable>
            <el-option
              v-for="item in vesselTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <div class="table-container bg-white p-4 rounded shadow-sm">
      <el-table
        v-loading="loading"
        :data="vesselList"
        border
        stripe
        style="width: 100%; margin-top: 20px;"
        :header-cell-style="{ backgroundColor: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="mmsi" label="MMSI号" width="120"></el-table-column>
        <el-table-column prop="cnName" label="中文名称" width="150"></el-table-column>
        <el-table-column prop="vesselName" label="英文名称" width="150"></el-table-column>
        <el-table-column label="船舶类型" width="100">
          <template slot-scope="scope">
            {{ formatVesselType(scope.row.vesselType) }}
          </template>
        </el-table-column>
        <el-table-column label="船舶尺寸" width="180">
          <template slot-scope="scope">
            长:{{ scope.row.length || '未知' }}{{ scope.row.length ? 'm' : '' }}, 
            宽:{{ scope.row.width || '未知' }}{{ scope.row.width ? 'm' : '' }}, 
            吃水:{{ scope.row.depth || '未知' }}{{ scope.row.depth ? 'm' : '' }}
          </template>
        </el-table-column>
        <el-table-column prop="contacts" label="联系方式" width="200">
          <template slot-scope="scope">
            {{ scope.row.contacts }}{{ scope.row.contactNumber }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建年份" width="100">
          <template slot-scope="scope">
            {{ scope.row.createTime ? new Date(scope.row.createTime).getFullYear() : '' }}
          </template>
        </el-table-column>
        <el-table-column prop="callSign" label="呼号" width="100"></el-table-column>
        <el-table-column fixed="right" label="操作" width="180">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-edit"
              @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              size="mini"
              type="danger"
              icon="el-icon-delete"
              @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { getVesselList, deleteVessel, getAllVesselTypes } from '@/api/vessel'

export default {
  name: 'VesselList',
  inject: ['registerNavButtons', 'unregisterNavButtons'],
  data() {
    return {
      loading: false,
      vesselList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        vesselName: '',
        mmsi: '',
        vesselType: ''
      },
      vesselTypeOptions: [], // 船舶类型选项
      // 导航按钮定义
      navButtons: [
        {
          id: 'add-vessel',
          text: '添加船舶',
          icon: 'el-icon-plus',
          type: 'success',
          handler: function() {
            this.$router.push('/vessel/management?actiontype=vessel-new&from=list');
          }.bind(this)
        }
      ]
    }
  },
  async created() {
    await this.fetchVesselTypes()
    this.fetchVesselList()
  },
  mounted() {
    // 注册导航按钮
    this.registerNavButtons(this.navButtons);
  },
  beforeDestroy() {
    // 注销导航按钮
    this.unregisterNavButtons();
  },
  methods: {
    // 获取船舶类型
    async fetchVesselTypes() {
      try {
        const response = await getAllVesselTypes()
        if (response && response.code === 200 && response.data) {
          // 将API返回的船舶类型数据转换为选项格式，使用typeCode作为value
          this.vesselTypeOptions = response.data.map(item => ({
            value: item.typeCode,  // 使用类型代码作为value
            label: item.typeName   // 显示中文名称
          }))
        } else {
          this.$message.error(response?.message || '获取船舶类型失败')
          this.vesselTypeOptions = []
        }
      } catch (error) {
        console.error('获取船舶类型出错:', error)
        this.$message.error('获取船舶类型失败')
        this.vesselTypeOptions = []
      }
    },

    // 获取船舶列表
    async fetchVesselList() {
      this.loading = true
      try {
        // 构建查询参数
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          mmsi: this.searchForm.mmsi || undefined,
          vesselName: this.searchForm.vesselName || undefined,
          vesselType: this.searchForm.vesselType || undefined
        }
        
        const response = await getVesselList(params)
        if (response && response.code === 200 && response.data) {
          this.vesselList = response.data.list || []
          this.total = response.data.total || 0
        } else {
          this.$message.error(response?.message || '获取船舶列表失败')
          this.vesselList = []
          this.total = 0
        }
      } catch (error) {
        console.error('获取船舶列表出错:', error)
        this.$message.error('获取船舶列表失败')
        this.vesselList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.fetchVesselList()
    },

    // 重置搜索条件
    resetSearch() {
      this.searchForm = {
        vesselName: '',
        mmsi: '',
        vesselType: ''
      }
      this.currentPage = 1
      this.fetchVesselList()
    },

    // 改变每页显示数量
    handleSizeChange(size) {
      this.pageSize = size
      this.fetchVesselList()
    },

    // 改变当前页
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchVesselList()
    },


    // 查看船舶
    handleView(row) {
      this.$router.push(`/vessel/management?id=${row.id}&view=true&from=list`)
    },

    // 编辑船舶
    handleEdit(row) {
      this.$router.push(`/vessel/management?id=${row.id}&from=list`)
    },

    // 删除船舶
    handleDelete(row) {
      this.$confirm('确定要删除该船舶信息吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteVessel(row.id)
          if (res && res.code === 200) {
            this.$message.success('删除成功')
            this.fetchVesselList()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (error) {
          console.error('删除船舶出错:', error)
          this.$message.error('删除船舶失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },

    // 格式化船舶类型
    formatVesselType(typeCode) {
      if (!typeCode) return '未知';
      
      // 尝试在选项中查找匹配项
      const type = this.vesselTypeOptions.find(item => item.value === typeCode);
      if (type) {
        return type.label;
      }
      
      // 如果没有在选项中找到匹配，直接返回原值
      return typeCode;
    }
  }
}
</script>

<style scoped>
.vessel-list-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
}

.search-box {
  margin-bottom: 20px;
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.add-vessel-btn {
  margin-left: auto;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style> 